import { ref, Ref } from "vue";
import { HoodApi } from "@/services/HoodApi";
import { useToast } from "./useToast";

type ContentType = "post" | "comment" | "proposal" | "proposalcomment";

const emojiMap: Record<ReactionType, string> = {
  like: "👍",
  love: "❤️",
  laugh: "😄",
  thinking: "🤔",
  clap: "👏",
  "100": "💯",
};

export function useReactions(
  contentType: ContentType,
  objectId: Ref<number> | number
) {
  const reactions = ref<ReactionSummary[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const toast = useToast();

  const getObjectId = (): number => {
    return typeof objectId === "number" ? objectId : objectId.value;
  };

  const fetchReactions = async () => {
    try {
      const data = await HoodApi.get_reactions_for_object(
        contentType,
        getObjectId()
      );
      // Convert object to array format
      reactions.value = Object.entries(data).map(([type, summary]) => ({
        reaction_type: type as ReactionType,
        count: summary.count,
        user_reacted: summary.user_reacted,
        users: summary.users,
      }));
    } catch (e) {
      console.error("Failed to fetch reactions:", e);
      error.value = "Failed to load reactions";
    }
  };

  const toggleReaction = async (reactionType: ReactionType) => {
    if (loading.value) return;

    loading.value = true;
    error.value = null;

    const emoji = emojiMap[reactionType];
    const existingReaction = reactions.value.find(
      (r) => r.reaction_type === reactionType && r.user_reacted
    );

    try {
      const result = await HoodApi.toggle_reaction({
        content_type: contentType,
        object_id: getObjectId(),
        reaction_type: reactionType,
      });

      // Optimistically update the local state
      const existingIndex = reactions.value.findIndex(
        (r) => r.reaction_type === reactionType
      );

      if ("message" in result && result.message === "Reaction removed") {
        // Reaction was removed
        if (existingIndex !== -1) {
          const existing = reactions.value[existingIndex];
          if (existing.count <= 1) {
            // Remove the reaction type entirely
            reactions.value.splice(existingIndex, 1);
          } else {
            // Decrement count
            existing.count--;
            existing.user_reacted = false;
          }
        }
        // Subtle feedback for removal - no toast needed
      } else {
        // Reaction was added
        if (existingIndex !== -1) {
          // Increment existing
          reactions.value[existingIndex].count++;
          reactions.value[existingIndex].user_reacted = true;
        } else {
          // Add new reaction type
          reactions.value.push({
            reaction_type: reactionType,
            count: 1,
            user_reacted: true,
          });
        }
        // Show toast for adding reaction
        toast.success(`${emoji} Reaction added!`, 1500);
      }
    } catch (e) {
      console.error("Failed to toggle reaction:", e);
      error.value = "Failed to update reaction";
      toast.error("Couldn't update reaction. Try again.");
      // Refresh to get correct state
      await fetchReactions();
    } finally {
      loading.value = false;
    }
  };

  const totalReactions = (): number => {
    return reactions.value.reduce((sum, r) => sum + r.count, 0);
  };

  const hasUserReacted = (): boolean => {
    return reactions.value.some((r) => r.user_reacted);
  };

  return {
    reactions,
    loading,
    error,
    fetchReactions,
    toggleReaction,
    totalReactions,
    hasUserReacted,
  };
}
